import React from "react";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import QuoteForm from "../components/QuoteForm";
import { COMPANY } from "../lib/data";

const Contact = () => {
    return (
        <main data-testid="contact-page" className="bg-background">
            {/* HEADER */}
            <section className="px-6 md:px-12 lg:px-24 pt-24 md:pt-32 pb-12 border-b border-border">
                <div className="font-mono text-xs tracking-[0.3em] uppercase text-accent mb-4">
                    Contact
                </div>
                <h1 className="font-display font-black text-5xl sm:text-7xl lg:text-8xl uppercase tracking-tighter leading-[0.85]">
                    Let's
                    <br />
                    <span className="text-accent">build.</span>
                </h1>
                <p className="mt-8 max-w-2xl text-lg text-muted-foreground">
                    Tell us about your project. Send a complete RFP, ask a quick question, or
                    request a bid package — our team will follow up.
                </p>
            </section>

            {/* CONTENT */}
            <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
                <div className="grid lg:grid-cols-12 gap-12">
                    {/* INFO */}
                    <aside className="lg:col-span-4">
                        <div className="border border-border bg-card p-8">
                            <div className="font-mono text-xs tracking-[0.3em] uppercase text-accent mb-6">
                                Direct
                            </div>
                            <div className="space-y-6">
                                <ContactItem
                                    icon={<Phone className="w-5 h-5" />}
                                    label="Call us"
                                    value={COMPANY.phone}
                                    href={`tel:${COMPANY.phone}`}
                                    testid="contact-phone"
                                />
                                <ContactItem
                                    icon={<Mail className="w-5 h-5" />}
                                    label="Email"
                                    value={COMPANY.email}
                                    href={`mailto:${COMPANY.email}`}
                                    testid="contact-email"
                                />
                                <ContactItem
                                    icon={<MapPin className="w-5 h-5" />}
                                    label="Office"
                                    value={`${COMPANY.address} · ${COMPANY.region}`}
                                    testid="contact-address"
                                />
                                <ContactItem
                                    icon={<Clock className="w-5 h-5" />}
                                    label="Hours"
                                    value="Mon–Thu · 7:30 — 4:30 · Fri · 7:30 — 4:00"
                                    testid="contact-hours"
                                />
                            </div>
                        </div>

                        <div className="mt-6 border-2 border-accent bg-secondary text-secondary-foreground p-8 relative">
                            <div className="absolute -top-3 left-4 bg-accent text-accent-foreground px-3 py-1 font-mono text-[10px] tracking-[0.3em] uppercase font-bold">
                                Subcontractors
                            </div>
                            <h3 className="mt-3 font-display font-black text-3xl uppercase tracking-tight">
                                MWC Plan Room
                            </h3>
                            <p className="mt-3 text-secondary-foreground/75 text-sm leading-relaxed">
                                Active bid documents, project plans, and specs for our subcontractor partners — one click away.
                            </p>
                            <a
                                href="https://www.mwcplanroom.com/"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="plan-room-link"
                                className="mt-6 inline-flex items-center gap-2 bg-accent text-accent-foreground px-6 py-4 font-bold uppercase tracking-wider hover:bg-background hover:text-foreground transition-colors w-full sm:w-auto justify-center"
                            >
                                Open Plan Room →
                            </a>
                        </div>
                    </aside>

                    {/* FORM */}
                    <div className="lg:col-span-8">
                        <QuoteForm source="contact_page_form" />
                    </div>
                </div>
            </section>

            {/* MAP STRIP */}
            <section className="border-t border-border" data-testid="map-section">
                <div className="aspect-[16/6] w-full bg-muted relative overflow-hidden">
                    <iframe
                        title="Mark Wilson Construction Office Location"
                        src="https://www.google.com/maps?q=5799+E+Clinton+Ave%2C+Fresno%2C+CA+93727&output=embed"
                        className="absolute inset-0 w-full h-full"
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                    />
                </div>
            </section>
        </main>
    );
};

const ContactItem = ({ icon, label, value, href, testid }) => {
    const inner = (
        <>
            <div className="text-accent mt-1 shrink-0">{icon}</div>
            <div>
                <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-muted-foreground">
                    {label}
                </div>
                <div className="mt-1 font-medium text-foreground break-words">{value}</div>
            </div>
        </>
    );
    if (href) {
        return (
            <a
                href={href}
                data-testid={testid}
                className="flex items-start gap-4 hover:text-accent transition-colors"
            >
                {inner}
            </a>
        );
    }
    return (
        <div data-testid={testid} className="flex items-start gap-4">
            {inner}
        </div>
    );
};

export default Contact;
